/**
 * MUI date/time pickers build their toolbar from standard MUI components
 * (Typography for the date/time text, Button for the clickable digits/AM-PM).
 *
 * Our global brand overrides for those components leak into the picker and
 * break its built-in layout:
 *  - Brand Typography variants (h3/h4/h5/subtitle1/subtitle2) shrink the
 *    date/time text and force uppercase.
 *  - MuiButton size styles add a fixed height/minWidth/lineHeight, which
 *    misaligns the time digits, the ":" separator and the AM/PM column.
 *
 * These overrides do nothing more than restore MUI's default values for the
 * picker toolbar, so the component renders as designed. They are not bespoke
 * styling — if MUI changes its defaults, these should track them.
 */

const MUI_DEFAULT_TOOLBAR_TYPOGRAPHY = {
  h3: {
    fontSize: '3rem',
    fontWeight: 400,
    lineHeight: 1.167,
    letterSpacing: 0,
    textTransform: 'none',
  },
  h4: {
    fontSize: '2.125rem',
    fontWeight: 400,
    lineHeight: 1.235,
    letterSpacing: '0.00735em',
    textTransform: 'none',
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 400,
    lineHeight: 1.334,
    letterSpacing: 0,
    textTransform: 'none',
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
    textTransform: 'none',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
    textTransform: 'none',
  },
} as const;

/** MUI sets AM/PM labels to 17px via `ampmLabel`, not subtitle2's 14px — see DateTimePickerToolbar.js */
const AMPM_LABEL_FONT_SIZE_PX = 17;

export const pickersToolbarTextStyleOverrides = {
  '&.MuiTypography-h3': MUI_DEFAULT_TOOLBAR_TYPOGRAPHY.h3,
  '&.MuiTypography-h4': MUI_DEFAULT_TOOLBAR_TYPOGRAPHY.h4,
  '&.MuiTypography-h5': MUI_DEFAULT_TOOLBAR_TYPOGRAPHY.h5,
  '&.MuiTypography-subtitle1': MUI_DEFAULT_TOOLBAR_TYPOGRAPHY.subtitle1,
  // Exclude ampmLabel — it uses 17px, not subtitle2's 14px.
  '&.MuiTypography-subtitle2:not(.MuiDateTimePickerToolbar-ampmLabel)':
    MUI_DEFAULT_TOOLBAR_TYPOGRAPHY.subtitle2,
  '&.MuiDateTimePickerToolbar-ampmLabel': {
    fontSize: AMPM_LABEL_FONT_SIZE_PX,
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
    textTransform: 'none',
  },
};

export const dateTimePickerToolbarStyleOverrides = {
  ampmLabel: {
    fontSize: AMPM_LABEL_FONT_SIZE_PX,
  },
};

/**
 * Undo the global MuiButton size styling (fixed height/minWidth/lineHeight) so
 * the toolbar buttons size to their text, exactly like MUI's PickersToolbarButton
 * default (padding: 0, minWidth: 16, textTransform: none). The compound selector
 * outranks the single-class global `.MuiButton-sizeMedium` rule.
 */
export const pickersToolbarButtonStyleOverrides = {
  '&.MuiButton-root': {
    padding: 0,
    minWidth: 16,
    minHeight: 0,
    height: 'auto',
    lineHeight: 'normal',
    textTransform: 'none',
  },
};

/**
 * Desktop time columns: MUI fixes each column at 56px (exactly the item width),
 * leaving no room for a scrollbar. On Windows the classic scrollbar takes ~17px
 * inside that box and sits over the numbers; the docs only escape this on
 * overlay-scrollbar systems. The `::after` spacer (height ≈ column − one item)
 * also leaves a large empty area below the last value.
 *
 * Let the column size to its content with a stable, thin scrollbar gutter so the
 * track never overlaps the numbers or shifts the layout, and drop the spacer.
 */
export const multiSectionDigitalClockSectionStyleOverrides = (theme: {
  palette: { action: { disabled: string } };
}) => ({
  width: 'auto',
  overflowY: 'auto' as const,
  scrollbarGutter: 'stable',
  scrollbarWidth: 'thin' as const,
  '&::after': {
    display: 'none',
  },
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 3,
    backgroundColor: theme.palette.action.disabled,
  },
});
